import { ContextMenuHandler, type IMenuItem } from "@asup/context-menu";
import React, { useState } from "react";
import { getScaffoldContextMenuTarget } from "../../scaffold/getScaffoldContextMenuTarget";
import { ArsTextJustification } from "../table";
import { useArsListing } from "./ArsListingContextProvider";
import {
  ARSL_ADD_BLANK_COLUMN,
  ARSL_ADD_ROW,
  ARSL_ALIGN_TEXT,
  ARSL_REMOVE_COLUMN,
  ARSL_REMOVE_ROW,
  ARSL_SET_VIEW_COLUMN_PROPERTIES_ID,
  type ArsListingColumn,
  HEADER_ROW_ID,
} from "./interface";

interface ArsListingContextMenuProviderProps<T> {
  children: React.ReactNode;
  selectedColumns: ArsListingColumn<T>[];
}

/**
 * Wraps ArsListing scaffold content and provides context-menu behaviour.
 *
 * @param props - Component props
 * @param props.children - Child scaffold content rendered inside context-menu handler
 * @param props.selectedColumns - Current selected listing columns
 * @returns Context-menu wrapper for listing content
 */
export const ArsListingContextMenuProvider = <T,>({
  children,
  selectedColumns,
}: ArsListingContextMenuProviderProps<T>): React.ReactElement => {
  const { state, dispatch } = useArsListing<T>();
  const [contextMenuTarget, setContextMenuTarget] = useState<
    { colId: string; rowId: string } | undefined
  >(undefined);
  if (!state) return <>{children}</>;

  /**
   * Function to blur active element
   */
  const blurActiveElement = (): void => {
    /* istanbul ignore else */
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  /**
   * Captures scaffold row and column ids before opening the context menu.
   *
   * @param event - Context menu event fired from scaffold cells
   */
  const handleScaffoldContextMenuCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    setContextMenuTarget(getScaffoldContextMenuTarget(event.target));
  };

  /**
   * Dispatches text-alignment changes for the selected context-menu column.
   *
   * @param alignment - Desired text alignment
   * @param isHeader - Whether alignment applies to header cells
   */
  const handleChangeTextAlignment = (alignment: ArsTextJustification, isHeader: boolean): void => {
    blurActiveElement();
    /* istanbul ignore else */
    if (!state.settings.disabled && contextMenuTarget) {
      dispatch({
        operation: ARSL_ALIGN_TEXT,
        colId: contextMenuTarget.colId,
        alignment,
        isHeader,
      });
    }
  };

  /**
   * Adds a blank column at at the index provided
   *
   * @param before - whether to insert the column before or after the current column
   */
  const handleAddBlankColumn = (before: boolean): void => {
    blurActiveElement();
    /* istanbul ignore else */
    if (!state.settings.disabled && contextMenuTarget) {
      const ix =
        state.bodyData.arsListingColumns.findIndex((col) => col.id === contextMenuTarget.colId) +
        (before ? 0 : 1);
      dispatch({
        operation: ARSL_ADD_BLANK_COLUMN,
        colIx: ix,
        colId: crypto.randomUUID(),
      });
    }
  };

  /**
   * Adds a new row after the currently targeted context-menu row.
   *
   * @param ellipsis - Whether the inserted row is an ellipsis row
   */
  const handleAddRow = (ellipsis: boolean): void => {
    blurActiveElement();
    /* istanbul ignore else */
    if (!state.settings.disabled && contextMenuTarget) {
      dispatch({
        operation: ARSL_ADD_ROW,
        rowId: contextMenuTarget.rowId,
        type: ellipsis ? "ellipsis" : "default",
      });
    }
  };

  /**
   * Removes the currently targeted body row when allowed.
   */
  const handleRemoveRow = (): void => {
    blurActiveElement();
    /* istanbul ignore else */
    if (!state.settings.disabled && contextMenuTarget && state.bodyData.arsBodyRows.length > 1) {
      const bodyRowIndex = state.bodyData.arsBodyRows.findIndex(
        (row) => row.id === contextMenuTarget.rowId,
      );
      /* istanbul ignore else */
      if (bodyRowIndex !== -1) {
        dispatch({
          operation: ARSL_REMOVE_ROW,
          rowId: contextMenuTarget.rowId,
        });
      }
    }
  };

  /**
   * Removes the currently targeted column when allowed.
   */
  const handleRemoveColumn = (): void => {
    blurActiveElement();
    /* istanbul ignore else */
    if (!state.settings.disabled && contextMenuTarget && selectedColumns.length > 1) {
      dispatch({
        operation: ARSL_REMOVE_COLUMN,
        colId: contextMenuTarget.colId,
      });
    }
  };

  const isHeaderRow = contextMenuTarget?.rowId === HEADER_ROW_ID;
  const isRowActionDisabled =
    state.settings.disabled || contextMenuTarget === undefined || isHeaderRow;
  const contextMenuColumn = state.bodyData.arsListingColumns.find(
    (col) => col.id === contextMenuTarget?.colId,
  );

  const arslContextMenuItems: IMenuItem[] = [
    {
      label: "Show column properties",
      disabled: state.settings.disabled || contextMenuTarget === undefined,
      action: () => {
        dispatch({
          operation: ARSL_SET_VIEW_COLUMN_PROPERTIES_ID,
          colId: contextMenuTarget?.colId,
        });
      },
    },
    {
      label: "Justify text",
      disabled: state.settings.disabled || contextMenuTarget === undefined,
      group: [
        {
          label: "Left",
          action: () => handleChangeTextAlignment(ArsTextJustification.Left, isHeaderRow),
          selected: isHeaderRow
            ? contextMenuColumn?.headerTextAlignment === ArsTextJustification.Left
            : contextMenuColumn?.bodyTextAlignment === ArsTextJustification.Left,
        },
        {
          label: "Centre",
          action: () => handleChangeTextAlignment(ArsTextJustification.Center, isHeaderRow),
          selected: isHeaderRow
            ? contextMenuColumn?.headerTextAlignment === ArsTextJustification.Center
            : contextMenuColumn?.bodyTextAlignment === ArsTextJustification.Center,
        },
        ...(contextMenuTarget?.rowId !== HEADER_ROW_ID
          ? [
              {
                label: "Decimal",
                action: () => handleChangeTextAlignment(ArsTextJustification.Decimal, isHeaderRow),
                selected: contextMenuColumn?.bodyTextAlignment === ArsTextJustification.Decimal,
              },
            ]
          : []),
        {
          label: "Right",
          action: () => handleChangeTextAlignment(ArsTextJustification.Right, isHeaderRow),
          selected: isHeaderRow
            ? contextMenuColumn?.headerTextAlignment === ArsTextJustification.Right
            : contextMenuColumn?.bodyTextAlignment === ArsTextJustification.Right,
        },
      ],
    },
    {
      label: "Add blank column",
      disabled: state.settings.disabled,
      group: [
        { label: "Before", action: () => handleAddBlankColumn(true) },
        { label: "After", action: () => handleAddBlankColumn(false) },
      ],
    },
    {
      label: "Add row below",
      disabled: isRowActionDisabled,
      group: [
        { label: "Display", action: () => handleAddRow(false) },
        { label: "Ellipsis", action: () => handleAddRow(true) },
      ],
    },
    {
      label: "Remove row",
      action: handleRemoveRow,
      disabled: isRowActionDisabled || state.bodyData.arsBodyRows.length <= 1,
    },
    {
      label: "Remove column",
      action: handleRemoveColumn,
      disabled:
        state.settings.disabled || contextMenuTarget === undefined || selectedColumns.length <= 1,
    },
  ];

  return (
    <ContextMenuHandler
      menuItems={arslContextMenuItems}
      onContextMenuCapture={handleScaffoldContextMenuCapture}
    >
      {children}
    </ContextMenuHandler>
  );
};

ArsListingContextMenuProvider.displayName = "ArsListingContextMenuProvider";
